import { getDB, STORES, type QueuedOrder } from './db';

/**
 * Add a transaction to the offline queue
 * @param transaction - The queued order to add
 * @param organizationLimit - Optional limit on queued transactions (-1 for unlimited, undefined for no limit check)
 * @throws Error if queue is full and limit is exceeded
 */
export const addTransaction = async (
  transaction: QueuedOrder,
  organizationLimit?: number
): Promise<void> => {
  const db = await getDB();
  const tx = db.transaction(STORES.SALES_QUEUE, 'readwrite');
  const store = tx.objectStore(STORES.SALES_QUEUE);

  // Check limit before adding (if limit is set and not unlimited)
  if (organizationLimit !== undefined && organizationLimit !== -1) {
    const existingCount = await store.count();
    if (existingCount >= organizationLimit) {
      throw new Error(
        `Offline queue full (limit: ${organizationLimit}). Please sync your orders or upgrade your plan.`
      );
    }
  }

  await store.put(transaction);
  await tx.done;
};

/**
 * Remove a transaction from the offline queue by localId
 */
export const removeTransaction = async (localId: string): Promise<void> => {
  const db = await getDB();
  const tx = db.transaction(STORES.SALES_QUEUE, 'readwrite');
  const store = tx.objectStore(STORES.SALES_QUEUE);

  await store.delete(localId);
  await tx.done;
};

/**
 * Get all transactions from the offline queue
 */
export const getAllTransactions = async (locationId?: string): Promise<QueuedOrder[]> => {
  const db = await getDB();

  if (locationId) {
    // Use index to filter by locationId
    return await db.getAllFromIndex(STORES.SALES_QUEUE, 'locationId', locationId);
  }

  // Get all transactions if no locationId specified
  return await db.getAll(STORES.SALES_QUEUE);
};

/**
 * Clear all transactions from the offline queue
 */
export const clearTransactions = async (): Promise<void> => {
  const db = await getDB();
  await db.clear(STORES.SALES_QUEUE);
};

/**
 * Get a single transaction by localId
 */
export const getTransaction = async (localId: string): Promise<QueuedOrder | undefined> => {
  const db = await getDB();
  return await db.get(STORES.SALES_QUEUE, localId);
};

/**
 * Update a transaction in the offline queue
 */
export const updateTransaction = async (localId: string, updates: Partial<QueuedOrder>): Promise<void> => {
  const db = await getDB();
  const tx = db.transaction(STORES.SALES_QUEUE, 'readwrite');
  const store = tx.objectStore(STORES.SALES_QUEUE);

  const existing = await store.get(localId);
  if (existing) {
    const updated = { ...existing, ...updates };
    await store.put(updated);
  }

  await tx.done;
};

const offlineQueueApi = {
  addTransaction,
  removeTransaction,
  getAllTransactions,
  clearTransactions,
  getTransaction,
  updateTransaction,
};

export default offlineQueueApi;
